import React, { useState } from "react";
import { CircularProgress, Container } from "@mui/material";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import AppLayout from "../../components/AppLayout/AppLayout";
import ButtonNative from "../../components/StyledComponents/ButtonNative";
import { PDFDocument } from 'pdf-lib';
import { ReactSortable } from "react-sortablejs";
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;

const Merger = () => {
  const [pdfGroups, setPdfGroups] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(null); // New state for overlay

  const generateThumbnails = async (file, groupId) => {
    const buffer = await file.arrayBuffer();
    const bufferCopy = buffer.slice(0);
    
    const pdfDoc = await pdfjsLib.getDocument(buffer).promise;
    const sourceDoc = await PDFDocument.load(bufferCopy);
    const thumbnails = [];

    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const scale = (600 / viewport.width) * window.devicePixelRatio; // Higher quality
      const scaledViewport = page.getViewport({ scale });
      
      const canvas = document.createElement("canvas");
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;
      const context = canvas.getContext("2d");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";
      
      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
      }).promise;

      thumbnails.push({
        id: `${groupId}-${i}`,
        thumbnail: canvas.toDataURL('image/png', 1.0),
        pageNum: i - 1,
        originalSourceId: groupId
      });
    }
    
    return { id: groupId, fileName: file.name, pages: thumbnails, sourceDoc };
  };

  const handleFileUpload = async (event) => {
    setUploading(true);
    const newFiles = Array.from(event.target.files);
    
    try {
      const newGroups = await Promise.all(
        newFiles.map((file, index) => 
          generateThumbnails(file, `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`)
        )
      );
      setPdfGroups(prev => [...prev, ...newGroups]);
    } catch (error) {
      console.error("Error processing files:", error);
    } finally {
      setUploading(false);
    }
  };

  const handlePageDelete = (groupId, pageId) => {
    setPdfGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          pages: group.pages.filter(page => page.id !== pageId)
        };
      }
      return group;
    }).filter(group => group.pages.length > 0));
  };

  const handlePdfDelete = (groupId) => {
    setPdfGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const handlePageSort = (groupId, newPages) => {
    setPdfGroups(prev => prev.map(group => 
      group.id === groupId ? { ...group, pages: newPages } : group
    ));
  };

  const handlePdfSort = (newGroups) => {
    setPdfGroups(newGroups);
  };

  const mergeFiles = async () => {
    if (pdfGroups.length === 0) return;

    setUploading(true);
    try {
      const pdfDoc = await PDFDocument.create();

      for (const group of pdfGroups) {
        for (const page of group.pages) {
          const sourceGroup = pdfGroups.find(g => g.id === page.originalSourceId);
          if (!sourceGroup || !sourceGroup.sourceDoc) {
            throw new Error(`Source document missing for page from ${page.originalSourceId}`);
          }
          const [copiedPage] = await pdfDoc.copyPages(sourceGroup.sourceDoc, [page.pageNum]);
          pdfDoc.addPage(copiedPage);
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "merged.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Merging failed:", error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <AppLayout>
      <Container maxWidth="xl" sx={{ textAlign: "center", padding: "40px" }}>
        <Card 
          sx={{ padding: "40px", marginBottom: "40px", minHeight: "600px" }}
          header={<CardHeader 
            avatar={<Icon name="document" />} 
            subtitleText="Upload, Arrange, and Merge PDF Pages" 
            titleText="Document Merger" 
          />}
        >
          <div style={{ 
            display: "flex", 
            gap: "20px", 
            margin: "20px", 
            justifyContent: "center"
          }}>
            <ButtonNative
              component="label"
              disabled={uploading}
              sx={{ padding: "10px 20px" }}
            >
              {uploading ? <CircularProgress size={24} /> : "Choose Files"}
              <input 
                type="file" 
                multiple 
                accept="application/pdf" 
                onChange={handleFileUpload}
                hidden
              />
            </ButtonNative>
            <ButtonNative
              onClick={mergeFiles}
              disabled={uploading || pdfGroups.length === 0}
              sx={{ padding: "10px 20px" }}
            >
              {uploading ? (
                <CircularProgress size={24} />
              ) : (
                `Merge ${pdfGroups.reduce((sum, g) => sum + g.pages.length, 0)} Page${pdfGroups.reduce((sum, g) => sum + g.pages.length, 0) === 1 ? '' : 's'}`
              )}
            </ButtonNative>
          </div>

          {pdfGroups.length > 0 && (
            <ReactSortable
              list={pdfGroups}
              setList={handlePdfSort}
              animation={200}
              style={{ 
                display: "flex",
                gap: "20px",
                overflowX: "auto",
                padding: "20px",
                margin: "20px",
                border: "1px solid #ccc",
                minHeight: "400px"
              }}
            >
              {pdfGroups.map(group => (
                <div 
                  key={group.id}
                  style={{
                    minWidth: "220px",
                    maxHeight: "500px",
                    overflowY: "auto",
                    border: "1px solid #eee",
                    padding: "15px",
                    background: "#f9f9f9",
                    cursor: "move"
                  }}
                >
                  <div style={{ 
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "10px"
                  }}>
                    <div style={{ 
                      fontWeight: "bold",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: "150px"
                    }}>
                      {group.fileName}
                    </div>
                    <button
                      onClick={() => handlePdfDelete(group.id)}
                      style={{
                        background: "#354a5f",
                        color: "white",
                        border: "none",
                        borderRadius: "50%",
                        width: "20px",
                        height: "20px",
                        cursor: "pointer"
                      }}
                    >
                      ×
                    </button>
                  </div>
                  <ReactSortable
                    list={group.pages}
                    setList={(newPages) => handlePageSort(group.id, newPages)}
                    animation={200}
                    group="pdf-pages"
                    style={{ display: "flex", flexDirection: "column", gap: "10px" }}
                  >
                    {group.pages.map(page => (
                      <div 
                        key={page.id}
                        style={{
                          position: "relative",
                          cursor: "move",
                          border: "1px solid #ddd",
                          padding: "5px"
                        }}
                      >
                        <img 
                          src={page.thumbnail} 
                          alt={`Page ${page.pageNum + 1}`} 
                          style={{ width: "100%", height: "auto", cursor: "pointer" }}
                          onClick={() => setSelectedPage(page)}
                        />
                        <div style={{ textAlign: "center" }}>
                          <small>Page {page.pageNum + 1} (from {pdfGroups.find(g => g.id === page.originalSourceId)?.fileName || 'unknown'})</small>
                        </div>
                        <button
                          onClick={() => handlePageDelete(group.id, page.id)}
                          style={{
                            position: "absolute",
                            top: "5px",
                            right: "5px",
                            background: "#354a5f",
                            color: "white",
                            border: "none",
                            borderRadius: "50%",
                            width: "20px",
                            height: "20px",
                            cursor: "pointer"
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </ReactSortable>
                </div>
              ))}
            </ReactSortable>
          )}
        </Card>

        {selectedPage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() => setSelectedPage(null)}
          >
            <img
              src={selectedPage.thumbnail}
              alt={`Preview of Page ${selectedPage.pageNum + 1}`}
              style={{
                maxWidth: "90%",
                maxHeight: "90%",
                objectFit: "contain",
              }}
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setSelectedPage(null)}
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                background: "#354a5f",
                color: "white",
                border: "none",
                borderRadius: "50%",
                width: "30px",
                height: "30px",
                cursor: "pointer",
              }}
            >
              ×
            </button>
          </div>
        )}
      </Container>
    </AppLayout>
  );
};

export default Merger;